/**
 * Patient Prescription detail page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { prescriptionAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationsContext';

const PatientPrescriptionDetail = () => {
  const { id } = useParams();
  const [prescription, setPrescription] = useState(null);
  const [loading, setLoading] = useState(true);
  const { on } = useNotifications();

  const loadDetail = useCallback(async () => {
    try {
      const res = await prescriptionAPI.getDetail(id);
      setPrescription(res.data || null);
    } catch (err) {
      toast.error(err?.message || 'Failed to load prescription.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => on('prescription_updated', (payload) => {
    if (!payload?.prescription_id || payload.prescription_id === id) {
      loadDetail();
    }
  }), [id, loadDetail, on]);

  if (loading) {
    return (
      <Layout title="Prescription" role="patient" showBack>
        <div className="p-4 pb-24 space-y-4">
          <div className="skeleton h-28 rounded-2xl"></div>
          <div className="skeleton h-56 rounded-2xl"></div>
        </div>
      </Layout>
    );
  }

  if (!prescription) {
    return (
      <Layout title="Prescription" role="patient" showBack>
        <div className="p-4 pb-24">
          <div className="card text-center py-12 text-gray-400">
            <p className="font-medium">Prescription not found</p>
            <p className="text-sm mt-1">The prescription may have been removed or is unavailable.</p>
          </div>
        </div>
      </Layout>
    );
  }

  const medications = Array.isArray(prescription.medications)
    ? prescription.medications
    : (prescription.medications ? [prescription.medications] : []);

  return (
    <Layout title="Prescription" role="patient" showBack>
      <div className="p-4 pb-24 space-y-4">
        <div className="card bg-purple-50 border-purple-200">
          <p className="text-xs text-purple-600 uppercase tracking-wide">Issued By</p>
          <p className="text-lg font-semibold text-gray-900 mt-1">
            Dr. {prescription.doctor_name || 'Unknown'}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {prescription.doctor_specialization || 'General Practice'}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {prescription.created_at ? new Date(prescription.created_at).toLocaleString() : ''}
          </p>
        </div>

        <div className="card space-y-4">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">Diagnosis</p>
            <p className="text-base font-medium text-gray-900 mt-1">
              {prescription.diagnosis || 'Not provided'}
            </p>
          </div>

          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Medications</p>
            {medications.length === 0 ? (
              <p className="text-sm text-gray-500">No medications listed.</p>
            ) : (
              <div className="space-y-3">
                {medications.map((med, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-3">
                    <p className="font-medium text-gray-900">{med?.name || `Medication ${index + 1}`}</p>
                    <p className="text-sm text-gray-600 mt-1">
                      {[med?.dosage, med?.frequency, med?.duration].filter(Boolean).join(' • ') || 'Instructions not provided'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wide">Instructions</p>
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
              {prescription.instructions || 'No patient instructions provided.'}
            </p>
          </div>

          {prescription.follow_up_date && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Follow-up Date</p>
              <p className="text-sm text-gray-700 mt-1">{prescription.follow_up_date}</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PatientPrescriptionDetail;